<template>
  <main class="services-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="content.seo" />

    <!-- Page intro -->
    <section class="services-hero" aria-labelledby="services-heading">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="7">
            <ScrollReveal>
              <h1 id="services-heading" class="services-heading">
                {{ content.intro.heading }}
              </h1>
              <p class="services-lead">{{ content.intro.lead }}</p>
            </ScrollReveal>
          </v-col>
          <v-col cols="12" md="5">
            <ScrollReveal>
              <v-img
                :src="content.intro.image.src"
                :alt="content.intro.image.alt"
                class="services-hero__img"
                height="280"
                cover
                loading="lazy"
              />
            </ScrollReveal>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!--
      Capability index: a jump-links grid so visitors can reach the capability
      they came for without scrolling the whole page.
    -->
    <section class="services-index" aria-labelledby="services-index-heading">
      <v-container>
        <h2 id="services-index-heading" class="services-index__heading">
          What we do
        </h2>
        <div class="services-index__grid">
          <a
            v-for="area in content.areas"
            :key="area.id"
            :href="`#${area.id}`"
            class="services-index__card"
          >
            <span class="services-index__card-title">{{ area.title }}</span>
            <span class="services-index__card-summary">{{ area.summary }}</span>
          </a>
        </div>
      </v-container>
    </section>

    <!-- Capability detail sections -->
    <section class="services-detail">
      <v-container>
        <ScrollReveal
          v-for="area in content.areas"
          :key="area.id"
        >
          <article
            :id="area.id"
            class="service-area"
            :aria-labelledby="`${area.id}-title`"
          >
            <h2 :id="`${area.id}-title`" class="service-area__title">
              {{ area.title }}
            </h2>

            <p
              v-for="(paragraph, index) in area.body"
              :key="index"
              class="service-area__body"
            >{{ paragraph }}</p>

            <ul v-if="area.points" class="service-area__points">
              <li
                v-for="(point, index) in area.points"
                :key="index"
              >{{ point }}</li>
            </ul>
          </article>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Closing CTA to Contact -->
    <section class="services-cta" aria-labelledby="services-cta-heading">
      <v-container>
        <ScrollReveal>
          <div class="services-cta__inner">
            <h2 id="services-cta-heading" class="services-cta__heading">
              {{ content.closingCta.heading }}
            </h2>
            <p class="services-cta__body">{{ content.closingCta.body }}</p>
            <CtaButton
              :to="content.closingCta.cta.to"
              :label="content.closingCta.cta.label"
            />
          </div>
        </ScrollReveal>
      </v-container>
    </section>
  </main>
</template>

<script>
// ServicesPage — the full ThinkSpatial capability set.
//
// Content comes from the structured module `client/src/content/services.js`,
// which mirrors the ThinkSpatial Capability Statement so the website and the
// capability document stay in step (Requirement 11.2).
//
// The page renders a jump-links index followed by one detail section per
// capability area, each with a stable anchor id so capabilities can be linked
// to directly (e.g. /services#laser-scanning).
//
// Uses the Options API to match the project's `plugin:vue/essential` preset.
import services from '@/content/services'
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import CtaButton from '@/components/CtaButton.vue'

export default {
  name: 'ServicesPage',
  components: {
    SeoHead,
    ScrollReveal,
    CtaButton,
  },
  data () {
    return {
      content: services,
    }
  },
}
</script>

<style lang="scss" scoped>
.services-hero {
  padding: 64px 0 32px;
}

.services-heading {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 16px;
}

.services-lead {
  font-size: 1.15rem;
  color: #4a4a4a;
  line-height: 1.7;
  max-width: 760px;
}

.services-hero__img {
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(65, 40, 27, 0.12);
}

// --- Capability index ---------------------------------------------------

.services-index {
  padding: 16px 0 48px;
}

.services-index__heading {
  font-size: 1.5rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 24px;
}

.services-index__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.services-index__card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: #fff;
  border: 1px solid rgba(65, 40, 27, 0.12);
  border-left: 4px solid #e7511e;
  border-radius: 12px;
  text-decoration: none;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.services-index__card:hover,
.services-index__card:focus-visible {
  box-shadow: 0 10px 24px rgba(65, 40, 27, 0.1);
  transform: translateY(-2px);
}

.services-index__card-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 6px;
}

.services-index__card-summary {
  font-size: 0.95rem;
  color: #4a4a4a;
  line-height: 1.5;
}

// --- Capability detail --------------------------------------------------

.services-detail {
  padding: 0 0 32px;
}

.service-area {
  max-width: 820px;
  padding: 32px 0;
  border-top: 1px solid rgba(65, 40, 27, 0.12);
  // Offset for the fixed app bar when jumping to an anchor.
  scroll-margin-top: 88px;
}

.service-area__title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.service-area__body {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.75;
  margin-bottom: 16px;
}

.service-area__points {
  margin: 8px 0 0 20px;
  color: #4a4a4a;
  font-size: 1.02rem;
  line-height: 1.7;
}

// --- Closing CTA -------------------------------------------------------

.services-cta {
  padding: 48px 0 80px;
  background-color: #f7f5f4;
}

.services-cta__inner {
  max-width: 640px;
}

.services-cta__heading {
  font-size: 1.8rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 12px;
}

.services-cta__body {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.7;
  margin-bottom: 24px;
}
</style>
